import { type FC } from "react";
import { Link } from "react-router-dom";

import { Headline, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { type QARuleOff, type RulePackageDetail } from "@api/rest/types-and-hooks";
import { StillBadge } from "@pages/QARules/StillBadge";
import { PackageType } from "@pages/QARules/constants";
import { humanizeEnum, orgQaHref, packageTypeOf, templateQaHref } from "@pages/QARules/qaRuleLabels";
import { TemplateStatus } from "@pages/Templates/constants";

interface PackageUsedByProps {
  pkg: RulePackageDetail;
  /** When set, rule-level offs are limited to this rule. */
  focusedRuleId?: number;
}

const LINK_CLASS = "text-sm text-text-primary hover:underline";

const ruleOffHref = (off: QARuleOff): string => {
  if (off.organization_id) return orgQaHref(off.organization_id);
  if (off.report_template_id) return templateQaHref(off.report_template_id);
  return "#";
};

/** Where a package applies and where it (or one of its rules) is turned off. */
export const PackageUsedBy: FC<PackageUsedByProps> = ({ pkg, focusedRuleId }) => {
  const packageType = packageTypeOf(pkg.package_type);
  const attachedOrgs = pkg.org_links.filter((link) => link.enabled);
  const attachedTemplates = pkg.template_links.filter((link) => link.enabled);
  const offOrgs = pkg.org_links.filter((link) => !link.enabled);
  const offTemplates = pkg.template_links.filter((link) => !link.enabled);
  const ruleNames = Object.fromEntries(pkg.rules.map((rule) => [rule.id, rule.name || "Untitled rule"]));
  const ruleOffs = pkg.rule_offs.filter((off) => !focusedRuleId || off.rule_id === focusedRuleId);

  return (
    <section className="flex flex-col gap-4">
      <Headline size="extra-small">Used by</Headline>

      {packageType === PackageType.BASE && (
        <Paragraph className="text-text-secondary">Every report — it&apos;s a base package.</Paragraph>
      )}
      {packageType === PackageType.INSURANCE && (
        <Paragraph className="text-text-secondary">
          Reports on templates whose payor is {pkg.payor_types.map(humanizeEnum).join(", ") || "(no payors set)"}.
        </Paragraph>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-1">
          <Small colorType="secondary">
            Attached to {attachedOrgs.length} org{attachedOrgs.length === 1 ? "" : "s"}
          </Small>
          {attachedOrgs.map((link) => (
            <Link key={link.organization_id} to={orgQaHref(link.organization_id)} className={LINK_CLASS}>
              {link.organization_name}
            </Link>
          ))}
        </div>
        <div className="flex flex-col gap-1">
          <Small colorType="secondary">
            Attached to {attachedTemplates.length} template{attachedTemplates.length === 1 ? "" : "s"}
          </Small>
          {attachedTemplates.map((link) => (
            <div key={link.report_template_id} className="flex items-center gap-2">
              <Link to={templateQaHref(link.report_template_id)} className={LINK_CLASS}>
                {link.report_template_name}
              </Link>
              {link.report_template_status === TemplateStatus.ARCHIVED && (
                <StillBadge text="Archived template" appearance="neutral" />
              )}
            </div>
          ))}
        </div>
      </div>

      {(offOrgs.length > 0 || offTemplates.length > 0 || ruleOffs.length > 0) && (
        <div className="flex flex-col gap-1">
          <Small colorType="secondary">Turned off</Small>
          {offOrgs.map((link) => (
            <Link key={`org-${link.organization_id}`} to={orgQaHref(link.organization_id)} className={LINK_CLASS}>
              Whole package off for {link.organization_name}
            </Link>
          ))}
          {offTemplates.map((link) => (
            <Link
              key={`template-${link.report_template_id}`}
              to={templateQaHref(link.report_template_id)}
              className={LINK_CLASS}
            >
              Whole package off for template {link.report_template_name}
            </Link>
          ))}
          {ruleOffs.map((off) => (
            <Link
              key={`${off.rule_id}-${off.organization_id ?? ""}-${off.report_template_id ?? ""}`}
              to={ruleOffHref(off)}
              className={LINK_CLASS}
            >
              &ldquo;{ruleNames[off.rule_id] ?? "Rule"}&rdquo; off for{" "}
              {off.organization_name ?? `template ${off.report_template_name}`}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};
